import React from "react";
import { Box, Typography, Button } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import ReplayIcon from "@mui/icons-material/Replay";
import BookmarkBorderRoundedIcon from "@mui/icons-material/BookmarkBorderRounded";
import AudioRecorder from "./AudioRecorder";
import { Auth, API, graphqlOperation } from "aws-amplify";
import { createQuestion } from "../graphql/mutations";

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.62)";
const INK_FAINT = "rgba(15, 17, 33, 0.42)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";

const MONO =
  "'JetBrains Mono', 'Söhne Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const SANS = "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";

const navBtnSx = {
  appearance: "none",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 0.75,
  py: 0.85,
  px: 1.5,
  border: `1px solid ${INK_HAIRLINE}`,
  borderRadius: "999px",
  backgroundColor: "#fff",
  color: INK,
  fontFamily: SANS,
  fontSize: "0.86rem",
  fontWeight: 500,
  transition:
    "transform 0.15s ease, border-color 0.15s ease, box-shadow 0.18s ease",
  "&:hover": {
    borderColor: "rgba(15, 17, 33, 0.32)",
    transform: "translateY(-1px)",
    boxShadow: "0 8px 18px -14px rgba(15, 17, 33, 0.4)"
  },
  "&:active": { transform: "translateY(0) scale(0.98)" },
  "&:disabled": {
    opacity: 0.35,
    cursor: "not-allowed",
    transform: "none",
    boxShadow: "none"
  }
};

const QuestionCard = ({
  question,
  index,
  total = 10,
  conversation,
  blobURL,
  audioKey,
  responseText,
  feedback,
  onBlobURLChange,
  onAudioKeyChange,
  onResponseTextChange,
  onConversationTextChange,
  onFeedbackChange,
  next,
  prev
}) => {
  const saveQuestion = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      if (!user || user.username === undefined) return;
      const newQuestion = {
        questionText: question,
        feedback,
        responseText,
        qID: user.username,
        conversation
      };
      await API.graphql(
        graphqlOperation(createQuestion, { input: newQuestion })
      );
    } catch (error) {
      console.error("Error creating question:", error);
    }
  };

  const handleRetry = () => {
    onBlobURLChange("");
    onAudioKeyChange("");
    onResponseTextChange("");
    onFeedbackChange("");
  };

  const hasContent = blobURL || audioKey || responseText || feedback;
  const progress = ((index + 1) / total) * 100;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 3
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flex: 1,
            minWidth: 0
          }}
        >
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: "0.72rem",
              fontWeight: 500,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: INK_FAINT,
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap"
            }}
          >
            Question {String(index + 1).padStart(2, "0")} ·{" "}
            {String(total).padStart(2, "0")}
          </Typography>
          <Box
            sx={{
              flex: 1,
              height: 2,
              borderRadius: "999px",
              backgroundColor: INK_HAIRLINE,
              position: "relative",
              overflow: "hidden",
              minWidth: 60
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                width: `${progress}%`,
                backgroundColor: INK,
                transition: "width 0.4s cubic-bezier(0.16, 1, 0.3, 1)"
              }}
            />
          </Box>
        </Box>
        <Button
          onClick={saveQuestion}
          startIcon={<BookmarkBorderRoundedIcon sx={{ fontSize: 16 }} />}
          sx={{
            fontFamily: SANS,
            fontSize: "0.82rem",
            fontWeight: 500,
            color: INK_SOFT,
            textTransform: "none",
            px: 1.25,
            "&:hover": {
              color: INK,
              backgroundColor: "transparent"
            }
          }}
        >
          Save
        </Button>
      </Box>

      <Typography
        sx={{
          fontFamily: SANS,
          fontSize: { xs: "1.4rem", md: "1.55rem" },
          fontWeight: 500,
          lineHeight: 1.3,
          letterSpacing: "-0.012em",
          color: INK,
          mb: 4
        }}
      >
        {question}
      </Typography>

      <AudioRecorder
        question={question}
        conversation={conversation}
        onConversationTextChange={onConversationTextChange}
        blobURL={blobURL}
        audioKey={audioKey}
        responseText={responseText}
        feedback={feedback}
        onBlobURLChange={onBlobURLChange}
        onAudioKeyChange={onAudioKeyChange}
        onResponseTextChange={onResponseTextChange}
        onFeedbackChange={onFeedbackChange}
      />

      <Box
        sx={{
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${INK_HAIRLINE}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap"
        }}
      >
        <Box
          component="button"
          onClick={prev}
          disabled={index === 0}
          sx={navBtnSx}
        >
          <NavigateBeforeIcon sx={{ fontSize: 18 }} />
          Previous
        </Box>
        {hasContent && (
          <Button
            onClick={handleRetry}
            startIcon={<ReplayIcon sx={{ fontSize: 16 }} />}
            sx={{
              fontFamily: SANS,
              fontSize: "0.82rem",
              fontWeight: 500,
              color: INK_FAINT,
              textTransform: "none",
              "&:hover": {
                color: INK,
                backgroundColor: "transparent"
              }
            }}
          >
            Retry this question
          </Button>
        )}
        <Box
          component="button"
          onClick={next}
          disabled={index >= total - 1}
          sx={navBtnSx}
        >
          Next
          <NavigateNextIcon sx={{ fontSize: 18 }} />
        </Box>
      </Box>
    </Box>
  );
};

export default QuestionCard;
